import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";

import { produce } from "immer";
import { deepEqual } from "@bridgesplit/utils";

export type DraftForm<T> = {
    // v1 (preserved, backward-compat)
    readonly value: T;
    readonly baseline: T;
    /** @deprecated Prefer update() for new call sites */
    set: Dispatch<SetStateAction<T>>;
    rebase: (fresh: T) => void;
    reset: () => void;

    // v2
    /** Mutate value via Immer recipe — no spreads needed */
    update: (recipe: (draft: T) => void) => void;
    /** Partially reset value to baseline via Immer recipe receiving (draft, baseline) */
    resetWith: (recipe: (draft: T, baseline: T) => void) => void;
    /** Reset a single top-level key to its baseline value */
    resetField: <K extends keyof T>(key: K) => void;
    /** True when value and baseline are semantically different */
    isDirty: boolean;
    /**
     * True when value and baseline differ on any key except the excluded ones.
     * Uses semantic (deep) equality for each top-level key.
     */
    isDirtyExcluding: <K extends keyof T>(...excludeKeys: K[]) => boolean;
};

export function useDraftForm<T>(initial: T): DraftForm<T> {
    const [value, setValue] = useState<T>(initial);
    const [baseline, setBaseline] = useState<T>(initial);

    // Synced every render so resetWith can always read the current baseline
    const baselineRef = useRef<T>(initial);
    baselineRef.current = baseline;

    const rebase = useCallback((fresh: T) => {
        setBaseline(fresh);
        setValue(fresh);
    }, []);

    const reset = useCallback(() => setValue(baseline), [baseline]);

    const update = useCallback((recipe: (draft: T) => void) => {
        setValue((prev) => produce(prev, recipe));
    }, []);

    const resetWith = useCallback((recipe: (draft: T, baseline: T) => void) => {
        setValue((prev) => produce(prev, (d) => recipe(d as T, baselineRef.current)));
    }, []);

    const resetField = useCallback(<K extends keyof T>(key: K) => {
        setValue((prev) =>
            produce(prev, (d) => {
                (d as T)[key] = baselineRef.current[key];
            })
        );
    }, []);

    const isDirty = !deepEqual(value, baseline);

    // Not memoized — cheap key-by-key check, computed at render time inside useMemo callers
    const isDirtyExcluding = <K extends keyof T>(...excludeKeys: K[]): boolean => {
        const exclude = new Set(excludeKeys as string[]);
        const valueRecord = value as Record<string, unknown>;
        const baselineRecord = baseline as Record<string, unknown>;
        const keys = new Set([...Object.keys(valueRecord), ...Object.keys(baselineRecord)]);
        for (const key of keys) {
            if (exclude.has(key)) continue;
            if (!deepEqual(valueRecord[key], baselineRecord[key])) return true;
        }
        return false;
    };

    return { value, baseline, set: setValue, rebase, reset, update, resetWith, resetField, isDirty, isDirtyExcluding };
}
